<template>
  <div class="validation-issues-button" v-if="issues.length > 0">
    <v-menu
      v-model="menu_open"
      :close-on-content-click="false"
      :max-width="menu_max_width"
      offset-y
      bottom
      left
    >
      <template #activator="{ on, attrs }">
        <v-tooltip bottom>
          <template #activator="{ on: tooltipOn, attrs: tooltipAttrs }">
            <v-btn
              icon
              v-bind="{ ...attrs, ...tooltipAttrs }"
              v-on="{ ...on, ...tooltipOn }"
              :disabled="issues.length === 0"
              :color="icon_color"
            >
              <v-badge
                :value="issues.length > 0"
                :content="issues.length"
                :color="badge_color"
                overlap
              >
                <v-icon :color="icon_color">
                  {{ icon }}
                </v-icon>
              </v-badge>
            </v-btn>
          </template>
          <span>{{ computed_tooltip_text }}</span>
        </v-tooltip>
      </template>

      <v-card>
        <v-card-title class="body-2 font-weight-bold pt-2 pb-1 pb-2">
          {{ title }}
        </v-card-title>
        
        <v-divider />
        
        <v-list dense class="py-1">
          <v-list-item
            v-for="(issue, index) in issues"
            :key="index"
            class="px-4 py-0"
          >
            <v-list-item-content>
              <v-list-item-title class="text-wrap font-weight-regular">
                {{ issue.text }}
              </v-list-item-title>
            </v-list-item-content>
            
            <v-list-item-action v-if="show_fix_button && issue.location" class="my-2">
              <v-btn
                :color="fix_button_color"
                small
                text
                @click="handleFix(issue)"
              >
                {{ fix_button_text }}
              </v-btn>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </v-card>
    </v-menu>
  </div>
</template>

<script>
/**
 * Displays a button with a badge showing the number of validation issues.
 * When clicked, shows a dropdown menu listing all issues with optional "Fix" buttons
 * that emit events to navigate to the problematic fields via data in the issue objects.
 * 
 * Companion for the guided-validation mixin.
 * 
 * @example
 * <validation-issues-button
 *   :issues="validation_warnings"
 *   title="Issues Preventing Submission"
 *   @fix-issue="handleFixIssue"
 * />
 */
export default {
  name: 'ValidationIssuesButton',

  props: {
    /**
     * Array of validation issue objects
     * @type {Array<{text: string, location?: Object}>}
     */
    issues: {
      type: Array,
      default: () => [],
    },

    /**
     * Title displayed at the top of the dropdown menu
     */
    title: {
      type: String,
      default: 'Validation Issues',
    },

    /**
     * Tooltip text shown on hover
     * If not provided, defaults to: "{count} issue(s) found"
     */
    tooltip_text: {
      type: String,
      default: null,
    },

    /**
     * Material Design Icon name for the button
     */
    icon: {
      type: String,
      default: 'mdi-alert-circle',
    },

    /**
     * Icon color
     */
    icon_color: {
      type: String,
      default: 'grey',
    },

    /**
     * Badge color
     */
    badge_color: {
      type: String,
      default: 'error',
    },

    /**
     * Whether to show the "Fix" button for issues with locations
     */
    show_fix_button: {
      type: Boolean,
      default: true,
    },

    /**
     * Text for the fix button
     */
    fix_button_text: {
      type: String,
      default: 'Fix it',
    },

    /**
     * Color of the fix button
     */
    fix_button_color: {
      type: String,
      default: 'primary',
    },

    /**
     * Maximum width of the dropdown menu
     */
    menu_max_width: {
      type: [String, Number],
      default: 400,
    },
  },

  data() {
    return {
      menu_open: false,
    };
  },

  computed: {
    computed_tooltip_text() {
      if (this.tooltip_text) {
        return this.tooltip_text;
      }
      const count = this.issues.length;
      return `${count} issue${count !== 1 ? 's' : ''} found`;
    },
  },

  methods: {
    handleFix(issue) {
      this.menu_open = false;
      this.$emit('fix-issue', issue);
    },
  },
};
</script>

<style scoped lang="scss">
.validation-issues-button {
  display: inline-block;
}

:deep(.v-list-item__title) {
  white-space: normal;
  line-height: 1.4;
}

:deep(.v-badge__badge) {
  font-size: 11px;
  height: 13px;
  min-width: unset;
  padding: 0 4px;
  line-height: 1.2;
  right: -5px !important;
  top: -5px !important;
}
</style>
